import React, { useContext, useEffect, useState } from 'react'
import classNames from 'classnames'
import { Modal } from 'react-bootstrap'
import { useParams } from 'react-router-dom'
import {
  Button,
  useErrorService,
  paymentType as paymentTypeConst,
} from '@abroad/components'
import {
  CoachingPlanDetailsModal,
  IndividualSOWForm,
  formInitialValues as initialValues,
} from '../coachingPlan'
import { OrgEcoIdsContext } from '../../utils/contexts'
import { isNumber } from '../../utils/utility'
import API from '../../utils/API'
import ErrorModal from '../common/ErrorModal'
import { canBulkAssignUsePlatformScheduling } from '../scheduling/schedulingPreconditions'

const AssignSOWSection = ({
  showModal = false,
  setShowModal = () => {},
  usersHasOneCoach = false,
  userCoachMapping = [],
  usersHasNoCoach = false,
  coachId = null,
  showCoachModal = false,
  setShowCoachModal = () => {},
  pairingCoaches = [],
  pairingSchedulingMeta = null,
  type = null,
  onResetAndRefresh = null,
  onSuccess = null,
}) => {
  const { ids, setIsLoadNewData, companyAndEcoInfo, setIds } =
    useContext(OrgEcoIdsContext)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)
  const [formValues, setFormValues] = useState({})
  const [formInitialValues, setInitialValues] = useState(initialValues)
  const [showErrorModal, setShowErrorModal] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const ErrorService = useErrorService()
  // const history = useHistory()
  const { companyName, ecoSystemName } = useParams()
  // Track if we're coming back from coach pairing modal (to preserve form data)
  const preserveFormDataRef = React.useRef(false)
  const prevShowModalRef = React.useRef(showModal)

  const handleBackToPairing = (currentFormValues = {}) => {
    // Preserve form data when going back to pairing
    if (currentFormValues && Object.keys(currentFormValues).length > 0) {
      setFormValues(currentFormValues)
    }
    preserveFormDataRef.current = true
    setShowModal(false)
    setShowCoachModal(true)
  }

  const handleBulkAssignSOW = async (formValues) => {
    // Check if we have individual coach assignments or a single coach
    const hasIndividualCoaches = userCoachMapping.some((user) => user?.coachId)
    const hasSingleCoach = coachId

    if (!hasIndividualCoaches && !hasSingleCoach) {
      ErrorService.showError(new Error('No coach selected'))
      return false
    }

    // Basic form validation
    if (!formValues.planName) {
      ErrorService.showError(new Error('Plan name is required'))
      return false
    }

    setIsSubmitting(true)

    try {
      const params = {
        isPaymentRequired:
          formValues.isPaymentRequired === 'true' ? true : false,
        // SOW Details
        // coachId: coachId,
        planName: formValues.planName,
        sowType: formValues.sowType || 'REGULAR',
        ...(formValues.sowType === 'QUEST' &&
          formValues.questId && { questId: formValues.questId }),
        // Billing
        paymentCycle: formValues.paymentCycle,

        ...(formValues.paymentCycle !== paymentTypeConst.RECURRING && {
          onetimeAmount: formValues.onetimeAmount,
        }),

        ...((formValues.paymentCycle === paymentTypeConst.ONETIME ||
          (formValues?.invoiceEvery && formValues?.numberOfBillingCycle)) && {
          totalDurationOfContract: formValues.totalDurationOfContract,
        }),

        ...(formValues.paymentCycle === paymentTypeConst.RECURRING && {
          invoiceEvery: isNumber(formValues.invoiceEvery)
            ? Number(formValues.invoiceEvery)
            : '',
          numberOfBillingCycle: formValues.numberOfBillingCycle,
          subscriptionAmount: formValues.subscriptionAmount,
          upfrontFee: formValues.upfrontFee,
        }),
        typeOfCoachingCycle: formValues.typeOfCoachingCycle,

        billingIndefinite:
          formValues.paymentCycle !== paymentTypeConst.ONETIME
            ? formValues.numberOfBillingCycle
              ? false
              : true
            : formValues.totalDurationOfContract
            ? false
            : true,

        // Coaching
        sessionPerCoachingCycle: formValues.sessionPerCoachingCycle,
        numberOfCoachingCycle: Number(formValues.numberOfCoachingCycle),
        sessionDuration: formValues.sessionDuration,
        includeGrowthSession: formValues.includeGrowthSession,
        ...(formValues.includeGrowthSession && {
          growthSessionDuration: formValues.growthSessionDuration,
          growthCoachingRateType: formValues.growthCoachingRateType,
          growthCoachingRate: formValues.growthCoachingRate,
        }),
        // report360: formValues.report360 ? formValues.reportPlan360 : null,
        // impactReport: formValues.impactReport ? formValues.impactReportPlan : null,

        coachingRate: formValues.coachingRate,
        coachingRateType: formValues.coachingRateType,
        ...((formValues.numberOfBillingCycle ||
          formValues.paymentCycle === paymentTypeConst.ONETIME) && {
          totalSession: formValues.totalSession,
        }),
        timeZone: formValues.clientTimeZone,
        planStartDate: formValues?.planStartDate,
        ...(formValues.isConditionalSchedulingEnabled && {
          isConditionalSchedulingEnabled: true,
          schedulingConditions: formValues.schedulingConditions,
        }),
        // Scheduling Type
        isPlatformScheduling:
          formValues.isPlatformScheduling === 'true' ||
          formValues.isPlatformScheduling === true ||
          (formValues.isPlatformScheduling !== false &&
            formValues.isPlatformScheduling !== 'false'),
        includeBioInMail: formValues.includeBioInMail === true,
        // Extra
        // ...(formValues.isPaymentRequired === true && {
        //   isReadyForPayment: formValues.isReadyForPayment || false,
        // }),
      }

      // Remove empty or undefined values
      Object.keys(params).forEach(
        (key) =>
          (params[key] === '' || params[key] === undefined) &&
          delete params[key],
      )

      // Prepare user-coach mapping
      const userCoachMappingArray = userCoachMapping.map((user) => ({
        clientId: user?.clientId || user?._id,
        coachId: user?.coachId || coachId, // Use individual coach ID from mapping, fallback to single coachId
      }))

      if (userCoachMappingArray.length === 0) {
        throw new Error('No users selected for SOW assignment')
      }

      // Make the API call
      const apiPayload = {
        sowData: {
          ...params,
          ...(formValues.isPaymentRequired === 'true' && {
            isReadyForPayment: formValues.isReadyForPayment || false,
          }),
        },
        clientCoachMappingData: userCoachMappingArray,
      }

      const response = await API.AdminAPI.common.clientCoachBulkPairing(
        apiPayload,
      )

      // Check if response has error status (response.data.status for axios responses)
      const responseStatus = response?.data?.status || response?.status
      if (responseStatus && typeof responseStatus === 'object') {
        if (responseStatus.success === false && responseStatus.message) {
          // Show error modal with the message
          setErrorMessage(responseStatus.message)
          setShowErrorModal(true)
          return false
        }
      }

      // Check for success - response.data.status.success === true or response.status === true
      const isSuccess =
        (responseStatus && responseStatus.success === true) ||
        response?.status === true ||
        response?.data?.status === true

      if (isSuccess) {
        // Close modals and refresh data
        setShowModal(false)
        setShowConfirmationModal(false)
        setIsLoadNewData(true)
        if (onSuccess && typeof onSuccess === 'function') {
          onSuccess()
        }
        return true
      }
      return false
    } catch (error) {
      console.error('Error in handleBulkAssignSOW:', error)

      // Check if error response has the specific format
      const errorStatus =
        error?.response?.data?.status || error?.data?.status || error?.status
      if (
        errorStatus &&
        typeof errorStatus === 'object' &&
        errorStatus.success === false &&
        errorStatus.message
      ) {
        // Show error modal with the message from response
        setErrorMessage(errorStatus.message)
        setShowErrorModal(true)
      } else {
        // Use default error handling
        ErrorService.showError(
          error
            ? error?.message || error
            : 'Failed to save SOW. Please try again.',
        )
      }
      return false
    } finally {
      setIsSubmitting(false)
    }
  }

  useEffect(() => {
    if (showModal) {
      // Check if we're coming back from coach pairing modal
      const wasHidden = !prevShowModalRef.current
      const isNowShown = showModal

      if (wasHidden && isNowShown) {
        // Modal is opening
        if (preserveFormDataRef.current && Object.keys(formValues).length > 0) {
          // Coming back from pairing modal - preserve form data
          // Coaches may have changed on the pairing screen, so refresh the bio eligibility inputs
          setInitialValues({
            ...initialValues,
            ...formValues,
            coachSchedulingFlagsByCoachId:
              pairingSchedulingMeta?.coachSchedulingFlagsByCoachId || {},
            bulkAssignCoachIds: [
              ...new Set(
                userCoachMapping
                  .map((user) => user?.coachId || coachId)
                  .filter(Boolean),
              ),
            ],
          })
          preserveFormDataRef.current = false
        } else {
          // Fresh open - set default values
          const assignedCoachIds = [
            ...new Set(
              userCoachMapping
                .map((user) => user?.coachId || coachId)
                .filter(Boolean),
            ),
          ]
          const coachSchedulingFlagsByCoachId =
            pairingSchedulingMeta?.coachSchedulingFlagsByCoachId || {}
          const forceManualScheduling =
            pairingSchedulingMeta?.forceManualScheduling ||
            !canBulkAssignUsePlatformScheduling(
              assignedCoachIds,
              coachSchedulingFlagsByCoachId,
            )
          const defaultValues = {
            ...initialValues,
            isPaymentRequired: initialValues.isPaymentRequired || 'false', // Default to no payment required
            planName: initialValues.planName,
            paymentCycle:
              initialValues.paymentCycle || paymentTypeConst.ONETIME,
            coachSchedulingFlagsByCoachId,
            bulkAssignCoachIds: assignedCoachIds,
            isPlatformScheduling: forceManualScheduling ? false : true,
            // Pre-select questId for networks (when quest creates network automatically)
            // Experience type SOW requires Platform Scheduling, so skip it when manual scheduling is forced
            ...(type === 'network' &&
              companyAndEcoInfo?.questId &&
              !forceManualScheduling && {
                questId: companyAndEcoInfo.questId,
                sowType: 'QUEST',
              }),
          }
          setInitialValues(defaultValues)
        }
      }

      // Update ref for next render
      prevShowModalRef.current = showModal
    } else {
      // Modal is closing
      if (prevShowModalRef.current && !preserveFormDataRef.current) {
        // Only reset form values if not preserving (i.e., closing by clicking outside)
        // Don't reset if going back to pairing (preserveFormDataRef.current will be true)
      }
      prevShowModalRef.current = showModal
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal, type, companyAndEcoInfo, formValues])

  const handleErrorModalClose = () => {
    setShowErrorModal(false)
    // Navigate to users page
    if (type === 'network' && ecoSystemName) {
      // history.push(`/admin/network/${ecoSystemName}/users`)
      window.location.href = `/admin/network/${ecoSystemName}/users`
    } else if (type === 'company' && companyName) {
      // history.push(`/admin/organization/${companyName}/users`)
      window.location.href = `/admin/organization/${companyName}/users`
    }
    // Close other modals
    setShowModal(false)
    setShowConfirmationModal(false)
  }

  return (
    <>
      <ErrorModal
        show={showErrorModal}
        onHide={handleErrorModalClose}
        message={errorMessage}
        title='SOW Creation Error'
      />
      <Button
        variant='saffron'
        size='md'
        className='line-height-normal assign-btn'
        style={{ height: '2rem' }}
        onClick={() => {
          if (!usersHasOneCoach) return
          setShowModal(true)
        }}
        isLoading={isSubmitting}
        disabled={ids?.length === 0 || !usersHasOneCoach}>
        Assign SOW
      </Button>
      {/* confirm coaching plan details */}
      <CoachingPlanDetailsModal
        showModal={showConfirmationModal}
        setShowModal={async (isOpen) => {
          if (!isOpen) {
            // If closing the modal, just update the state
            setShowConfirmationModal(false)
            setShowModal(true)
          } else {
            setShowConfirmationModal(true)
          }
        }}
        isLoading={isSubmitting}
        handleSubmit={async (values) => {
          try {
            // Use the values passed from the modal (which includes isReadyForPayment)
            const finalValues = values || formValues

            const success = await handleBulkAssignSOW(finalValues)
            if (success) {
              setShowConfirmationModal(false)
              setShowModal(false)
            }
            // If success is false, handleBulkAssignSOW will show the error modal
            return success
          } catch (error) {
            console.error('Error in confirmation modal submit:', error)

            // Check if error response has the specific format
            const errorStatus =
              error?.response?.data?.status ||
              error?.data?.status ||
              error?.status
            if (
              errorStatus &&
              typeof errorStatus === 'object' &&
              errorStatus.success === false &&
              errorStatus.message
            ) {
              // Show error modal with the message from response
              setErrorMessage(errorStatus.message)
              setShowErrorModal(true)
            } else {
              // Use default error handling
              ErrorService.showError(
                'Failed to process the request. Please try again.',
              )
            }
            return false
          }
        }}
        values={formValues}
        isBulkAssignSOWSection={true}
      />
      <Modal
        show={showModal}
        className={classNames(
          'border-modal custom__modal assign-sow sow-modal',
          {
            invisible: showConfirmationModal,
          },
        )}
        enforceFocus={false}
        size='lg'
        onHide={() => {
          // Only reset if not preserving form data (i.e., clicking outside, not going back to pairing)
          if (!preserveFormDataRef.current) {
            // Reset all state and refresh page when modal is closed
            setFormValues({})
            setInitialValues(initialValues)
            setIds([])
            setIsLoadNewData(true)
            setShowCoachModal(false)
            // Reset parent state if callback provided
            if (onResetAndRefresh && typeof onResetAndRefresh === 'function') {
              onResetAndRefresh()
            }
          } else {
            // Just close the modal, form data is preserved
            preserveFormDataRef.current = false
          }
          setShowModal(false)
        }}
        scrollable={true}
        centered>
        <Modal.Header className='s3 text-center'>Assign SOW</Modal.Header>
        <Modal.Body>
          {/* Individual SOW Form */}
          <IndividualSOWForm
            initialValues={formInitialValues}
            coaches={pairingCoaches}
            previewSample={
              userCoachMapping[0]
                ? {
                    clientId:
                      userCoachMapping[0]?.clientId || userCoachMapping[0]?._id,
                    coachId: userCoachMapping[0]?.coachId || coachId,
                  }
                : null
            }
            onBackToPairing={handleBackToPairing}
            onSubmit={async (values, { setSubmitting }) => {
              try {
                // Set form values
                setFormValues(values)

                // Handle submission based on payment status
                // Check if payment is required

                if (values.isPaymentRequired === 'true') {
                  setShowConfirmationModal(true)
                } else {
                  const success = await handleBulkAssignSOW(values)
                  if (success) {
                    // Form submitted successfully
                    setShowModal(false)
                  }
                }
              } catch (error) {
                console.error('Form submission error:', error)
                ErrorService.showError(
                  'Failed to submit the form. Please try again.',
                )
              } finally {
                // Always set submitting to false when done
                if (setSubmitting && typeof setSubmitting === 'function') {
                  setSubmitting(false)
                }
              }
            }}
            viewOnlyField={false}
            isPlanTerminated={false}
            isUpdatePlanLoading={isSubmitting}
            isBulkAssignSOWSection={true}
          />
        </Modal.Body>
      </Modal>
    </>
  )
}

export default AssignSOWSection
